import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api, euro } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import {
  LogOut, Plus, Trash2, Pencil, X, Upload, Bike, Package, Mail,
  Clock, Save, ExternalLink, Check, Lock, CalendarCheck,
} from "lucide-react";
import useSEO from "../hooks/useSEO";
import AgendaBeheer from "../components/AgendaBeheer";

const emptyMoto = {
  merk: "", model: "", bouwjaar: "", kilometerstand: "", meeteenheid: "km", cilinderinhoud: "", vermogen: "",
  brandstof: "", transmissie: "", kleur: "", prijs: "", beschrijving: "", opties: [],
  onderhoudshistoriek: "", status: "beschikbaar", images: [],
};
const emptyPart = { naam: "", omschrijving: "", prijs: "", beschikbaar: true, categorie: "", image: "" };

const fld = "w-full bg-[#0A0A0B] border border-[#2A2B31] px-3 py-2.5 text-sm text-white focus:outline-none focus:border-[#E10600]";
const lbl = "font-mono-tt text-[11px] uppercase tracking-wider text-[#9A9CA3] block mb-1";

function fileToBase64(file) {
  return new Promise((resolve, reject) => {
    const r = new FileReader();
    r.onload = () => resolve(r.result);
    r.onerror = reject;
    r.readAsDataURL(file);
  });
}

// Compress/resize images in the browser before upload. iPhone HEIC/HEIF photos
// are first converted to JPEG so they don't fail the canvas step.
async function compressImage(file, maxDim = 1600, quality = 0.82) {
  let src = file;
  const isHeic = /image\/(heic|heif)/i.test(file.type || "") || /\.hei[cf]$/i.test(file.name || "");
  if (isHeic) {
    const heic2any = (await import("heic2any")).default;
    const converted = await heic2any({ blob: file, toType: "image/jpeg", quality: 0.9 });
    src = Array.isArray(converted) ? converted[0] : converted;
  }
  if (!(src instanceof Blob) || !(src.type || "").startsWith("image/")) {
    return fileToBase64(file);
  }
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = (e) => {
      const img = new Image();
      img.onload = () => {
        let { width, height } = img;
        if (width > maxDim || height > maxDim) {
          if (width >= height) { height = Math.round((height * maxDim) / width); width = maxDim; }
          else { width = Math.round((width * maxDim) / height); height = maxDim; }
        }
        const canvas = document.createElement("canvas");
        canvas.width = width; canvas.height = height;
        canvas.getContext("2d").drawImage(img, 0, 0, width, height);
        resolve(canvas.toDataURL("image/jpeg", quality));
      };
      img.onerror = reject;
      img.src = e.target.result;
    };
    reader.onerror = reject;
    reader.readAsDataURL(src);
  });
}

/* ---------------- Login ---------------- */
function Login() {
  const { login } = useAuth();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [err, setErr] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setErr(""); setLoading(true);
    try { await login(email, password); }
    catch { setErr("Onjuiste inloggegevens"); }
    finally { setLoading(false); }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-[#0A0A0B] px-5" data-testid="beheer-login">
      <div className="w-full max-w-sm">
        <div className="text-center mb-8">
          <img src="/tt-logo.png" alt="TT Motorsports" className="h-16 mx-auto mb-4" />
          <div className="flex items-center justify-center gap-2 text-[#9A9CA3] font-mono-tt text-sm">
            <Lock size={14} /> Beheerpaneel
          </div>
        </div>
        <form onSubmit={submit} className="bg-[#141518] border border-[#2A2B31] p-8 space-y-4">
          <div>
            <label className={lbl}>E-mail</label>
            <input data-testid="login-email" type="email" required value={email} onChange={(e) => setEmail(e.target.value)} className={fld} />
          </div>
          <div>
            <label className={lbl}>Wachtwoord</label>
            <input data-testid="login-password" type="password" required value={password} onChange={(e) => setPassword(e.target.value)} className={fld} />
          </div>
          {err && <p className="text-[#E10600] text-sm">{err}</p>}
          <button data-testid="login-submit" disabled={loading}
            className="w-full bg-[#E10600] hover:bg-[#B80500] disabled:opacity-60 text-white font-display uppercase font-700 py-3 transition-colors">
            {loading ? "Bezig..." : "Inloggen"}
          </button>
        </form>
      </div>
    </div>
  );
}

/* ---------------- Motorcycle form modal ---------------- */
function MotoForm({ initial, onClose, onSaved }) {
  const [f, setF] = useState({ ...emptyMoto, ...initial, meeteenheid: initial?.meeteenheid || "km", opties: (initial?.opties || []).join(", ") });
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState("");
  const [uploading, setUploading] = useState(false);
  const set = (k, v) => setF((p) => ({ ...p, [k]: v }));

  const addImages = async (files) => {
    setUploading(true);
    try {
      const arr = [];
      for (const file of files) arr.push(await compressImage(file));
      setF((p) => ({ ...p, images: [...(p.images || []), ...arr] }));
    } catch {
      setErr("Foto verwerken mislukt. Probeer opnieuw of gebruik een JPG/PNG.");
    } finally { setUploading(false); }
  };

  const save = async () => {
    setSaving(true); setErr("");
    const payload = {
      ...f,
      bouwjaar: f.bouwjaar ? parseInt(f.bouwjaar) : null,
      kilometerstand: f.kilometerstand !== "" ? parseInt(f.kilometerstand) : null,
      meeteenheid: f.meeteenheid || "km",
      prijs: f.prijs !== "" ? parseFloat(f.prijs) : null,
      opties: typeof f.opties === "string" ? f.opties.split(",").map((s) => s.trim()).filter(Boolean) : f.opties,
    };
    try {
      if (initial?.id) await api.put(`/motorcycles/${initial.id}`, payload);
      else await api.post("/motorcycles", payload);
      onSaved();
    } catch (e) {
      setErr("Opslaan mislukt. Controleer je internet en probeer opnieuw. Tip: gebruik minder of kleinere foto's.");
    } finally { setSaving(false); }
  };

  return (
    <div className="fixed inset-0 z-[70] bg-black/70 flex items-start justify-center overflow-y-auto py-10 px-4" data-testid="moto-form-modal">
      <div className="bg-[#141518] border border-[#2A2B31] w-full max-w-3xl">
        <div className="flex items-center justify-between px-6 py-4 border-b border-[#2A2B31] sticky top-0 bg-[#141518]">
          <h3 className="font-display uppercase font-700 text-xl text-white">{initial?.id ? "Motor bewerken" : "Nieuwe motor"}</h3>
          <button onClick={onClose} data-testid="moto-form-close" className="text-[#9A9CA3] hover:text-white"><X size={22} /></button>
        </div>
        <div className="p-6 space-y-4">
          <div className="grid sm:grid-cols-2 gap-4">
            <div><label className={lbl}>Merk *</label><input data-testid="mf-merk" value={f.merk} onChange={(e) => set("merk", e.target.value)} className={fld} /></div>
            <div><label className={lbl}>Model *</label><input data-testid="mf-model" value={f.model} onChange={(e) => set("model", e.target.value)} className={fld} /></div>
            <div><label className={lbl}>Bouwjaar</label><input type="number" value={f.bouwjaar} onChange={(e) => set("bouwjaar", e.target.value)} className={fld} /></div>
            <div>
              <label className={lbl}>Meeteenheid</label>
              <select data-testid="mf-meeteenheid" value={f.meeteenheid || "km"} onChange={(e) => set("meeteenheid", e.target.value)} className={fld}>
                <option value="km">Kilometerstand (km)</option>
                <option value="uren">Urenstand (uren)</option>
              </select>
            </div>
            <div><label className={lbl}>{(f.meeteenheid || "km") === "uren" ? "Urenstand" : "Kilometerstand"}</label><input data-testid="mf-kilometerstand" type="number" value={f.kilometerstand} onChange={(e) => set("kilometerstand", e.target.value)} className={fld} /></div>
            <div><label className={lbl}>Cilinderinhoud</label><input value={f.cilinderinhoud} onChange={(e) => set("cilinderinhoud", e.target.value)} className={fld} placeholder="bv. 450cc" /></div>
            <div><label className={lbl}>Vermogen</label><input value={f.vermogen} onChange={(e) => set("vermogen", e.target.value)} className={fld} placeholder="bv. 55 pk" /></div>
            <div><label className={lbl}>Brandstof</label><input value={f.brandstof} onChange={(e) => set("brandstof", e.target.value)} className={fld} /></div>
            <div><label className={lbl}>Transmissie</label><input value={f.transmissie} onChange={(e) => set("transmissie", e.target.value)} className={fld} /></div>
            <div><label className={lbl}>Kleur</label><input value={f.kleur} onChange={(e) => set("kleur", e.target.value)} className={fld} /></div>
            <div><label className={lbl}>Prijs (€)</label><input type="number" value={f.prijs} onChange={(e) => set("prijs", e.target.value)} className={fld} /></div>
          </div>
          <div><label className={lbl}>Beschrijving</label><textarea rows={3} value={f.beschrijving} onChange={(e) => set("beschrijving", e.target.value)} className={fld} /></div>
          <div><label className={lbl}>Onderhoudshistoriek</label><textarea rows={2} value={f.onderhoudshistoriek} onChange={(e) => set("onderhoudshistoriek", e.target.value)} className={fld} /></div>
          <div><label className={lbl}>Opties (komma-gescheiden)</label><input data-testid="mf-opties" value={f.opties} onChange={(e) => set("opties", e.target.value)} className={fld} placeholder="Nieuwe banden, Handkappen" /></div>
          <div>
            <label className={lbl}>Status</label>
            <select data-testid="mf-status" value={f.status} onChange={(e) => set("status", e.target.value)} className={fld}>
              <option value="beschikbaar">Beschikbaar</option>
              <option value="verkocht">Verkocht</option>
            </select>
          </div>
          <div>
            <label className={lbl}>Foto's</label>
            <label className="inline-flex items-center gap-2 border border-dashed border-[#2A2B31] hover:border-[#E10600] px-4 py-3 cursor-pointer text-sm text-[#9A9CA3] transition-colors">
              <Upload size={16} /> {uploading ? "Verwerken..." : "Foto's uploaden"}
              <input data-testid="mf-images" type="file" accept="image/*,.heic,.heif" multiple className="hidden" onChange={(e) => addImages(Array.from(e.target.files))} />
            </label>
            {f.images?.length > 0 && (
              <div className="grid grid-cols-5 gap-2 mt-3">
                {f.images.map((img, i) => (
                  <div key={i} className="relative aspect-square border border-[#2A2B31] group">
                    <img src={img} alt="" className="w-full h-full object-cover" />
                    <button onClick={() => set("images", f.images.filter((_, x) => x !== i))}
                      className="absolute top-0 right-0 bg-[#E10600] text-white p-1"><X size={12} /></button>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
        <div className="px-6 py-4 border-t border-[#2A2B31] flex items-center justify-end gap-3">
          {err && <p data-testid="mf-error" className="text-[#E10600] text-sm mr-auto">{err}</p>}
          <button onClick={onClose} className="border border-[#2A2B31] text-white px-5 py-2.5 font-display uppercase text-sm">Annuleer</button>
          <button data-testid="mf-save" onClick={save} disabled={saving || uploading || !f.merk || !f.model}
            className="bg-[#E10600] hover:bg-[#B80500] disabled:opacity-60 text-white px-6 py-2.5 font-display uppercase font-700 text-sm flex items-center gap-2">
            <Save size={16} /> {saving ? "Opslaan..." : "Opslaan"}
          </button>
        </div>
      </div>
    </div>
  );
}

/* ---------------- Part form modal ---------------- */
function PartForm({ initial, onClose, onSaved }) {
  const [f, setF] = useState({ ...emptyPart, ...initial });
  const [saving, setSaving] = useState(false);
  const set = (k, v) => setF((p) => ({ ...p, [k]: v }));

  const uploadImg = async (file) => set("image", await compressImage(file));

  const save = async () => {
    setSaving(true);
    const payload = { ...f, prijs: f.prijs !== "" && f.prijs !== null ? parseFloat(f.prijs) : null };
    try {
      if (initial?.id) await api.put(`/parts/${initial.id}`, payload);
      else await api.post("/parts", payload);
      onSaved();
    } finally { setSaving(false); }
  };

  return (
    <div className="fixed inset-0 z-[70] bg-black/70 flex items-start justify-center overflow-y-auto py-10 px-4" data-testid="part-form-modal">
      <div className="bg-[#141518] border border-[#2A2B31] w-full max-w-lg">
        <div className="flex items-center justify-between px-6 py-4 border-b border-[#2A2B31]">
          <h3 className="font-display uppercase font-700 text-xl text-white">{initial?.id ? "Onderdeel bewerken" : "Nieuw onderdeel"}</h3>
          <button onClick={onClose} className="text-[#9A9CA3] hover:text-white"><X size={22} /></button>
        </div>
        <div className="p-6 space-y-4">
          <div><label className={lbl}>Naam *</label><input data-testid="pf-naam" value={f.naam} onChange={(e) => set("naam", e.target.value)} className={fld} /></div>
          <div><label className={lbl}>Omschrijving</label><textarea rows={3} value={f.omschrijving} onChange={(e) => set("omschrijving", e.target.value)} className={fld} /></div>
          <div className="grid grid-cols-2 gap-4">
            <div><label className={lbl}>Prijs (€)</label><input type="number" value={f.prijs ?? ""} onChange={(e) => set("prijs", e.target.value)} className={fld} /></div>
            <div><label className={lbl}>Categorie</label><input value={f.categorie} onChange={(e) => set("categorie", e.target.value)} className={fld} placeholder="Onderdelen / Accessoires" /></div>
          </div>
          <label className="flex items-center gap-2 text-sm text-white cursor-pointer">
            <input type="checkbox" checked={f.beschikbaar} onChange={(e) => set("beschikbaar", e.target.checked)} className="accent-[#E10600] w-4 h-4" data-testid="pf-beschikbaar" />
            Beschikbaar / op voorraad
          </label>
          <div>
            <label className={lbl}>Foto</label>
            <label className="inline-flex items-center gap-2 border border-dashed border-[#2A2B31] hover:border-[#E10600] px-4 py-3 cursor-pointer text-sm text-[#9A9CA3]">
              <Upload size={16} /> Foto uploaden
              <input type="file" accept="image/*,.heic,.heif" className="hidden" onChange={(e) => e.target.files[0] && uploadImg(e.target.files[0])} />
            </label>
            {f.image && <img src={f.image} alt="" className="mt-3 w-32 h-32 object-cover border border-[#2A2B31]" />}
          </div>
        </div>
        <div className="px-6 py-4 border-t border-[#2A2B31] flex justify-end gap-3">
          <button onClick={onClose} className="border border-[#2A2B31] text-white px-5 py-2.5 font-display uppercase text-sm">Annuleer</button>
          <button data-testid="pf-save" onClick={save} disabled={saving || !f.naam}
            className="bg-[#E10600] hover:bg-[#B80500] disabled:opacity-60 text-white px-6 py-2.5 font-display uppercase font-700 text-sm flex items-center gap-2">
            <Save size={16} /> {saving ? "Opslaan..." : "Opslaan"}
          </button>
        </div>
      </div>
    </div>
  );
}

/* ---------------- Dashboard ---------------- */
function Dashboard() {
  const { email, logout } = useAuth();
  const [tab, setTab] = useState("motoren");
  const [motos, setMotos] = useState([]);
  const [parts, setParts] = useState([]);
  const [messages, setMessages] = useState([]);
  const [appointments, setAppointments] = useState([]);
  const [settings, setSettings] = useState(null);
  const [motoForm, setMotoForm] = useState(null);
  const [partForm, setPartForm] = useState(null);
  const [savedSettings, setSavedSettings] = useState(false);
  const [icalCopied, setIcalCopied] = useState(false);
  const icalUrl = settings ? `${process.env.REACT_APP_BACKEND_URL}/api/appointments/calendar.ics?token=${settings.ical_token || ""}` : "";

  const loadMotos = () => api.get("/motorcycles").then((r) => setMotos(r.data));
  const loadParts = () => api.get("/parts").then((r) => setParts(r.data));
  const loadMessages = () => api.get("/messages").then((r) => setMessages(r.data)).catch(() => {});
  const loadAppointments = () => api.get("/appointments").then((r) => setAppointments(r.data)).catch(() => {});
  const loadSettings = () => api.get("/settings").then((r) => setSettings(r.data));

  useEffect(() => { loadMotos(); loadParts(); loadMessages(); loadAppointments(); loadSettings(); }, []);

  const delMoto = async (id) => { if (window.confirm("Deze motor verwijderen?")) { await api.delete(`/motorcycles/${id}`); loadMotos(); } };
  const delPart = async (id) => { if (window.confirm("Dit onderdeel verwijderen?")) { await api.delete(`/parts/${id}`); loadParts(); } };
  const delMsg = async (id) => { if (window.confirm("Dit bericht verwijderen?")) { await api.delete(`/messages/${id}`); loadMessages(); } };
  const delApt = async (id) => { if (window.confirm("Deze afspraak verwijderen?")) { await api.delete(`/appointments/${id}`); loadAppointments(); } };
  const setAptStatus = async (id, status) => { await api.put(`/appointments/${id}/status`, { status }); loadAppointments(); };

  const saveSettings = async () => {
    await api.put("/settings", settings);
    setSavedSettings(true); setTimeout(() => setSavedSettings(false), 2000);
  };

  const tabs = [
    { id: "motoren", label: "Motoren", icon: Bike, count: motos.length },
    { id: "onderdelen", label: "Onderdelen", icon: Package, count: parts.length },
    { id: "afspraken", label: "Afspraken", icon: CalendarCheck, count: appointments.length },
    { id: "berichten", label: "Berichten", icon: Mail, count: messages.length },
    { id: "instellingen", label: "Instellingen", icon: Clock },
  ];

  return (
    <div className="min-h-screen bg-[#0A0A0B]" data-testid="beheer-dashboard">
      <header className="border-b border-[#2A2B31] bg-[#141518] sticky top-0 z-40">
        <div className="max-w-[1400px] mx-auto px-5 md:px-8 h-16 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <img src="/tt-logo.png" alt="TT" className="h-9" />
            <span className="font-display uppercase font-700 text-white hidden sm:block border-l border-[#2A2B31] pl-3">Beheer</span>
          </div>
          <div className="flex items-center gap-4">
            <a href="/" target="_blank" rel="noreferrer" className="text-[#9A9CA3] hover:text-white text-sm flex items-center gap-1.5 font-mono-tt"><ExternalLink size={14} /> Website</a>
            <span className="text-[#9A9CA3] text-sm font-mono-tt hidden md:inline">{email}</span>
            <button data-testid="logout-btn" onClick={logout} className="text-[#E10600] hover:text-white flex items-center gap-1.5 text-sm font-display uppercase"><LogOut size={16} /> Uitloggen</button>
          </div>
        </div>
      </header>

      <div className="max-w-[1400px] mx-auto px-5 md:px-8 py-8">
        <div className="flex flex-wrap gap-2 mb-8 border-b border-[#2A2B31]">
          {tabs.map((t) => (
            <button key={t.id} data-testid={`tab-${t.id}`} onClick={() => setTab(t.id)}
              className={`flex items-center gap-2 px-5 py-3 font-display uppercase font-600 text-sm border-b-2 -mb-px transition-colors ${
                tab === t.id ? "border-[#E10600] text-white" : "border-transparent text-[#9A9CA3] hover:text-white"
              }`}>
              <t.icon size={16} /> {t.label} {t.count !== undefined && <span className="text-xs text-[#5A5C63]">({t.count})</span>}
            </button>
          ))}
        </div>

        {/* MOTOREN */}
        {tab === "motoren" && (
          <div>
            <div className="flex justify-between items-center mb-6">
              <h2 className="heading-xl text-2xl text-white">Motoren beheren</h2>
              <button data-testid="add-moto" onClick={() => setMotoForm(emptyMoto)}
                className="bg-[#E10600] hover:bg-[#B80500] text-white px-5 py-2.5 font-display uppercase font-700 text-sm flex items-center gap-2"><Plus size={16} /> Nieuwe motor</button>
            </div>
            <div className="space-y-3">
              {motos.map((m) => (
                <div key={m.id} data-testid={`admin-moto-${m.id}`} className="bg-[#141518] border border-[#2A2B31] p-4 flex items-center gap-4">
                  <img src={m.images?.[0] || "https://images.unsplash.com/photo-1558981403-c5f9899a28bc?w=200"} alt="" className="w-20 h-16 object-cover border border-[#2A2B31]" />
                  <div className="flex-1 min-w-0">
                    <p className="font-display uppercase font-700 text-white">{m.merk} {m.model}</p>
                    <p className="text-[#9A9CA3] text-sm font-mono-tt">{m.bouwjaar || "—"} · {euro(m.prijs)} · <span className={m.status === "verkocht" ? "text-[#E10600]" : "text-green-500"}>{m.status}</span></p>
                  </div>
                  <button onClick={() => setMotoForm(m)} className="text-[#9A9CA3] hover:text-white p-2" data-testid={`edit-moto-${m.id}`}><Pencil size={18} /></button>
                  <button onClick={() => delMoto(m.id)} className="text-[#9A9CA3] hover:text-[#E10600] p-2" data-testid={`del-moto-${m.id}`}><Trash2 size={18} /></button>
                </div>
              ))}
              {motos.length === 0 && <p className="text-[#9A9CA3]">Nog geen motoren toegevoegd.</p>}
            </div>
          </div>
        )}

        {/* ONDERDELEN */}
        {tab === "onderdelen" && (
          <div>
            <div className="flex justify-between items-center mb-6">
              <h2 className="heading-xl text-2xl text-white">Onderdelen beheren</h2>
              <button data-testid="add-part" onClick={() => setPartForm(emptyPart)}
                className="bg-[#E10600] hover:bg-[#B80500] text-white px-5 py-2.5 font-display uppercase font-700 text-sm flex items-center gap-2"><Plus size={16} /> Nieuw onderdeel</button>
            </div>
            <div className="space-y-3">
              {parts.map((p) => (
                <div key={p.id} data-testid={`admin-part-${p.id}`} className="bg-[#141518] border border-[#2A2B31] p-4 flex items-center gap-4">
                  <img src={p.image || "https://images.unsplash.com/photo-1558981403-c5f9899a28bc?w=200"} alt="" className="w-16 h-16 object-cover border border-[#2A2B31]" />
                  <div className="flex-1 min-w-0">
                    <p className="font-display uppercase font-700 text-white">{p.naam}</p>
                    <p className="text-[#9A9CA3] text-sm font-mono-tt">{euro(p.prijs)} · {p.beschikbaar ? "op voorraad" : "uitverkocht"}</p>
                  </div>
                  <button onClick={() => setPartForm(p)} className="text-[#9A9CA3] hover:text-white p-2"><Pencil size={18} /></button>
                  <button onClick={() => delPart(p.id)} className="text-[#9A9CA3] hover:text-[#E10600] p-2"><Trash2 size={18} /></button>
                </div>
              ))}
              {parts.length === 0 && <p className="text-[#9A9CA3]">Nog geen onderdelen toegevoegd.</p>}
            </div>
          </div>
        )}

        {/* AFSPRAKEN */}
        {tab === "afspraken" && (
          <AgendaBeheer appointments={appointments} reload={loadAppointments} />
        )}

        {/* BERICHTEN */}
        {tab === "berichten" && (
          <div>
            <h2 className="heading-xl text-2xl text-white mb-6">Contactberichten</h2>
            <div className="space-y-3">
              {messages.map((msg) => (
                <div key={msg.id} className="bg-[#141518] border border-[#2A2B31] p-5" data-testid={`admin-msg-${msg.id}`}>
                  <div className="flex justify-between items-start gap-4 mb-2">
                    <div>
                      <p className="font-display uppercase font-700 text-white">{msg.onderwerp}</p>
                      <p className="text-sm text-[#9A9CA3] font-mono-tt">{msg.naam} · {msg.email} {msg.telefoon && `· ${msg.telefoon}`}</p>
                    </div>
                    <button onClick={() => delMsg(msg.id)} className="text-[#9A9CA3] hover:text-[#E10600] p-1"><Trash2 size={16} /></button>
                  </div>
                  <p className="text-[#B8B9BE] text-sm whitespace-pre-line">{msg.bericht}</p>
                  <p className="text-[10px] text-[#5A5C63] font-mono-tt mt-3">{new Date(msg.created_at).toLocaleString("nl-BE")}</p>
                </div>
              ))}
              {messages.length === 0 && <p className="text-[#9A9CA3]">Nog geen berichten ontvangen.</p>}
            </div>
          </div>
        )}

        {/* INSTELLINGEN */}
        {tab === "instellingen" && settings && (
          <div className="max-w-2xl">
            <h2 className="heading-xl text-2xl text-white mb-6">Contactgegevens & openingsuren</h2>
            <div className="bg-[#141518] border border-[#2A2B31] p-6 space-y-4">
              <div><label className={lbl}>Telefoon</label><input data-testid="set-telefoon" value={settings.telefoon} onChange={(e) => setSettings({ ...settings, telefoon: e.target.value })} className={fld} /></div>
              <div><label className={lbl}>E-mail</label><input value={settings.email} onChange={(e) => setSettings({ ...settings, email: e.target.value })} className={fld} /></div>
              <div><label className={lbl}>Adres</label><input value={settings.adres} onChange={(e) => setSettings({ ...settings, adres: e.target.value })} className={fld} /></div>
              <div><label className={lbl}>Introtekst</label><input value={settings.intro} onChange={(e) => setSettings({ ...settings, intro: e.target.value })} className={fld} /></div>
              <div>
                <label className={lbl}>Openingsuren</label>
                <div className="space-y-2">
                  {(settings.openingsuren || []).map((o, i) => (
                    <div key={i} className="grid grid-cols-[110px_1fr] gap-2">
                      <input value={o.dag} onChange={(e) => { const u = [...settings.openingsuren]; u[i] = { ...u[i], dag: e.target.value }; setSettings({ ...settings, openingsuren: u }); }} className={fld} />
                      <input value={o.uren} onChange={(e) => { const u = [...settings.openingsuren]; u[i] = { ...u[i], uren: e.target.value }; setSettings({ ...settings, openingsuren: u }); }} className={fld} />
                    </div>
                  ))}
                </div>
              </div>
              <button data-testid="save-settings" onClick={saveSettings}
                className="bg-[#E10600] hover:bg-[#B80500] text-white px-6 py-3 font-display uppercase font-700 text-sm flex items-center gap-2">
                {savedSettings ? <><Check size={16} /> Opgeslagen</> : <><Save size={16} /> Opslaan</>}
              </button>
            </div>

            <h2 className="heading-xl text-2xl text-white mt-10 mb-6">Agenda & beschikbaarheid</h2>
            <div className="bg-[#141518] border border-[#2A2B31] p-6 space-y-6">
              {/* Afspraaktypes */}
              <div>
                <label className={lbl}>Afspraaktypes & duur (minuten)</label>
                <div className="space-y-2">
                  {(settings.afspraak_types || []).map((t, i) => (
                    <div key={i} className="grid grid-cols-[1fr_90px_40px] gap-2">
                      <input value={t.naam} onChange={(e) => { const u = [...settings.afspraak_types]; u[i] = { ...u[i], naam: e.target.value }; setSettings({ ...settings, afspraak_types: u }); }} className={fld} />
                      <input type="number" step="15" value={t.duur} onChange={(e) => { const u = [...settings.afspraak_types]; u[i] = { ...u[i], duur: parseInt(e.target.value) || 0 }; setSettings({ ...settings, afspraak_types: u }); }} className={fld} />
                      <button onClick={() => setSettings({ ...settings, afspraak_types: settings.afspraak_types.filter((_, j) => j !== i) })} className="text-[#9A9CA3] hover:text-[#E10600]"><Trash2 size={16} /></button>
                    </div>
                  ))}
                </div>
                <button data-testid="add-type" onClick={() => setSettings({ ...settings, afspraak_types: [...(settings.afspraak_types || []), { naam: "Nieuw type", duur: 60 }] })} className="mt-2 text-xs text-[#E10600] flex items-center gap-1"><Plus size={13} /> Type toevoegen</button>
              </div>

              {/* Slot interval + capaciteit */}
              <div className="grid grid-cols-2 gap-3">
                <div><label className={lbl}>Interval starttijden (min)</label><input type="number" step="15" value={settings.slot_interval ?? 30} onChange={(e) => setSettings({ ...settings, slot_interval: parseInt(e.target.value) || 30 })} className={fld} /></div>
                <div><label className={lbl}>Max. "afgeven" per slot</label><input type="number" value={settings.max_afgeven_per_slot ?? 2} onChange={(e) => setSettings({ ...settings, max_afgeven_per_slot: parseInt(e.target.value) || 1 })} className={fld} /></div>
              </div>

              {/* Verlofdagen / feestdagen */}
              <div>
                <label className={lbl}>Verlof- & feestdagen (losse datums)</label>
                <div className="flex flex-wrap gap-2 mb-2">
                  {(settings.verlofdagen || []).map((d, i) => (
                    <span key={i} className="inline-flex items-center gap-1 bg-[#0A0A0B] border border-[#2A2B31] px-2 py-1 text-xs text-white">
                      {d} <button onClick={() => setSettings({ ...settings, verlofdagen: settings.verlofdagen.filter((_, j) => j !== i) })} className="text-[#9A9CA3] hover:text-[#E10600]"><X size={12} /></button>
                    </span>
                  ))}
                </div>
                <input data-testid="add-verlofdag" type="date" onChange={(e) => { if (e.target.value && !(settings.verlofdagen || []).includes(e.target.value)) setSettings({ ...settings, verlofdagen: [...(settings.verlofdagen || []), e.target.value] }); }} className={fld} />
              </div>

              {/* Verlofperiodes */}
              <div>
                <label className={lbl}>Sluitingsperiodes (bv. bouwverlof)</label>
                <div className="space-y-2">
                  {(settings.verlofperiodes || []).map((p, i) => (
                    <div key={i} className="grid grid-cols-[1fr_1fr_40px] gap-2 items-center">
                      <input type="date" value={p.van || ""} onChange={(e) => { const u = [...settings.verlofperiodes]; u[i] = { ...u[i], van: e.target.value }; setSettings({ ...settings, verlofperiodes: u }); }} className={fld} />
                      <input type="date" value={p.tot || ""} onChange={(e) => { const u = [...settings.verlofperiodes]; u[i] = { ...u[i], tot: e.target.value }; setSettings({ ...settings, verlofperiodes: u }); }} className={fld} />
                      <button onClick={() => setSettings({ ...settings, verlofperiodes: settings.verlofperiodes.filter((_, j) => j !== i) })} className="text-[#9A9CA3] hover:text-[#E10600]"><Trash2 size={16} /></button>
                    </div>
                  ))}
                </div>
                <button onClick={() => setSettings({ ...settings, verlofperiodes: [...(settings.verlofperiodes || []), { van: "", tot: "" }] })} className="mt-2 text-xs text-[#E10600] flex items-center gap-1"><Plus size={13} /> Periode toevoegen</button>
              </div>

              {/* Geblokkeerde blokken */}
              <div>
                <label className={lbl}>Geblokkeerde tijdsblokken (pauze / afwezig)</label>
                <div className="space-y-2">
                  {(settings.geblokkeerde_blokken || []).map((b, i) => (
                    <div key={i} className="grid grid-cols-[1fr_80px_80px_40px] gap-2 items-center">
                      <input type="date" value={b.datum || ""} onChange={(e) => { const u = [...settings.geblokkeerde_blokken]; u[i] = { ...u[i], datum: e.target.value }; setSettings({ ...settings, geblokkeerde_blokken: u }); }} className={fld} />
                      <input type="time" value={b.van || ""} onChange={(e) => { const u = [...settings.geblokkeerde_blokken]; u[i] = { ...u[i], van: e.target.value }; setSettings({ ...settings, geblokkeerde_blokken: u }); }} className={fld} />
                      <input type="time" value={b.tot || ""} onChange={(e) => { const u = [...settings.geblokkeerde_blokken]; u[i] = { ...u[i], tot: e.target.value }; setSettings({ ...settings, geblokkeerde_blokken: u }); }} className={fld} />
                      <button onClick={() => setSettings({ ...settings, geblokkeerde_blokken: settings.geblokkeerde_blokken.filter((_, j) => j !== i) })} className="text-[#9A9CA3] hover:text-[#E10600]"><Trash2 size={16} /></button>
                    </div>
                  ))}
                </div>
                <button onClick={() => setSettings({ ...settings, geblokkeerde_blokken: [...(settings.geblokkeerde_blokken || []), { datum: "", van: "", tot: "" }] })} className="mt-2 text-xs text-[#E10600] flex items-center gap-1"><Plus size={13} /> Blok toevoegen</button>
              </div>

              {/* Extra slots */}
              <div>
                <label className={lbl}>Extra beschikbare tijdsblokken (buiten openingsuren)</label>
                <div className="space-y-2">
                  {(settings.extra_slots || []).map((b, i) => (
                    <div key={i} className="grid grid-cols-[1fr_80px_80px_40px] gap-2 items-center">
                      <input type="date" value={b.datum || ""} onChange={(e) => { const u = [...settings.extra_slots]; u[i] = { ...u[i], datum: e.target.value }; setSettings({ ...settings, extra_slots: u }); }} className={fld} />
                      <input type="time" value={b.van || ""} onChange={(e) => { const u = [...settings.extra_slots]; u[i] = { ...u[i], van: e.target.value }; setSettings({ ...settings, extra_slots: u }); }} className={fld} />
                      <input type="time" value={b.tot || ""} onChange={(e) => { const u = [...settings.extra_slots]; u[i] = { ...u[i], tot: e.target.value }; setSettings({ ...settings, extra_slots: u }); }} className={fld} />
                      <button onClick={() => setSettings({ ...settings, extra_slots: settings.extra_slots.filter((_, j) => j !== i) })} className="text-[#9A9CA3] hover:text-[#E10600]"><Trash2 size={16} /></button>
                    </div>
                  ))}
                </div>
                <button onClick={() => setSettings({ ...settings, extra_slots: [...(settings.extra_slots || []), { datum: "", van: "", tot: "" }] })} className="mt-2 text-xs text-[#E10600] flex items-center gap-1"><Plus size={13} /> Extra blok toevoegen</button>
              </div>

              <div>
                <label className={lbl}>Agenda-synchronisatie (Google Calendar / Outlook)</label>
                <p className="text-xs text-[#9A9CA3] mb-2">Abonneer je agenda-app op onderstaande link. Alle (niet-geannuleerde) afspraken verschijnen dan automatisch in Google Calendar of Outlook en worden regelmatig bijgewerkt.</p>
                <div className="flex gap-2">
                  <input readOnly data-testid="ical-url" value={icalUrl}
                    onFocus={(e) => e.target.select()} className={`${fld} text-xs`} />
                  <button type="button" data-testid="ical-copy" onClick={() => { navigator.clipboard?.writeText(icalUrl); setIcalCopied(true); setTimeout(() => setIcalCopied(false), 2000); }}
                    className="shrink-0 border border-[#2A2B31] hover:border-[#E10600] text-white px-3 text-xs font-display uppercase flex items-center gap-1">
                    {icalCopied ? <Check size={14} /> : <ExternalLink size={14} />} {icalCopied ? "Gekopieerd" : "Kopieer"}
                  </button>
                </div>
                <p className="text-[11px] text-[#5A5C63] mt-1.5 font-mono-tt">Google Agenda → "Andere agenda's" → "Via URL". Outlook → "Agenda toevoegen" → "Abonneren via internet".</p>
              </div>

              <button data-testid="save-settings-2" onClick={saveSettings}
                className="bg-[#E10600] hover:bg-[#B80500] text-white px-6 py-3 font-display uppercase font-700 text-sm flex items-center gap-2">
                {savedSettings ? <><Check size={16} /> Opgeslagen</> : <><Save size={16} /> Opslaan</>}
              </button>
            </div>
          </div>
        )}
      </div>

      {motoForm && <MotoForm initial={motoForm} onClose={() => setMotoForm(null)} onSaved={() => { setMotoForm(null); loadMotos(); }} />}
      {partForm && <PartForm initial={partForm} onClose={() => setPartForm(null)} onSaved={() => { setPartForm(null); loadParts(); }} />}
    </div>
  );
}

export default function Beheer() {
  useSEO({ title: "Beheer — TT Motorsports", description: "Beheerpaneel TT Motorsports" });
  const { isAuthed } = useAuth();
  useNavigate();
  return isAuthed ? <Dashboard /> : <Login />;
}
